import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import './BlogDetails.scss'
// Adjust these asset paths to match your project (same icons as the finance page).
// import filterIcon from 'Assets/Icons/filter.svg'
// import exportIcon from 'Assets/Icons/export.svg'
// import arrowLeft from 'Assets/Icons/arrow-left.svg'
// import arrowRight from 'Assets/Icons/arrow-right.svg'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Users/filter.svg'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Users/export.svg'
import arrowLeft from 'BoostXpressAdmin/Assets/Icon/Users/arrow-left.png'
import arrowRight from 'BoostXpressAdmin/Assets/Icon/Users/arrow-right.svg'
import { CATEGORY_OPTIONS } from '../Blog'
import blogService from 'Services/blogService'
import { BlogListItem } from 'Models'
// import { handleSuccess, handleError } from '<your toast util path>'

type BlogCategory = 'all' | 'published' | 'draft' | 'scheduled'

type BlogCounts = Record<BlogCategory, number>

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 400

const categoryTabs: { key: BlogCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
  { key: 'scheduled', label: 'Scheduled' },
]

/**
 * Maps a tab to the backend `status` filter. `null` means "no backend
 * equivalent": the API has no scheduled state (publishing is immediate), so
 * the Scheduled tab renders an empty state and sends no request.
 */
const tabStatus: Record<BlogCategory, 'draft' | 'published' | undefined | null> = {
  all: undefined,
  published: 'published',
  draft: 'draft',
  scheduled: null,
}

const exportFileName = 'blog-articles'

const EMPTY_COUNTS: BlogCounts = { all: 0, published: 0, draft: 0, scheduled: 0 }

/**
 * Pulls a readable message out of an Axios-style error.
 *
 * @param error Whatever was thrown
 * @param fallback Message used when the error carries none
 */
const getErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as {
    response?: { data?: { message?: string | string[]; error?: string } }
    message?: string
  }
  const message = e.response?.data?.message
  if (Array.isArray(message)) return message.join(' ')
  return message || e.response?.data?.error || e.message || fallback
}

/**
 * Formats an ISO timestamp as "17 Oct 2026". Returns an em dash for missing
 * or invalid dates so a bad value never renders "Invalid Date".
 *
 * @param iso ISO 8601 timestamp
 */
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Initials for the author avatar ("Tradegrid Admin" -> "TA").
 *
 * @param name Author display name
 */
const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'TG'

/** Title-cases a stored lowercase category for display ("energy access" -> "Energy Access"). */
const getCategoryLabel = (value: string): string =>
  CATEGORY_OPTIONS.find((option) => option.value === value)?.label ??
  value.replace(/\b\w/g, (char) => char.toUpperCase())

const BlogDetails = () => {
  const navigate = useNavigate()

  const [activeCategory, setActiveCategory] = useState<BlogCategory>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const [articles, setArticles] = useState<BlogListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [tabCounts, setTabCounts] = useState<BlogCounts>(EMPTY_COUNTS)
  const [openMenuSlug, setOpenMenuSlug] = useState<string | null>(null)
  /** Bumped after a mutation (trash) to re-run the list and count queries. */
  const [reloadKey, setReloadKey] = useState(0)

  const tableSectionRef = useRef<HTMLDivElement | null>(null)
  const filtersRef = useRef<HTMLDivElement | null>(null)

  // Reset filters and paging whenever the tab changes.
  useEffect(() => {
    setSearchTerm('')
    setDebouncedSearch('')
    setCategoryFilter('')
    setShowFilters(false)
    setPageNumber(1)
    setOpenMenuSlug(null)
  }, [activeCategory])

  // Debounce the search box so we don't request on every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [searchTerm])

  // Close the row menu / filter panel when clicking elsewhere.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (!target?.closest('.blog-list__menu')) setOpenMenuSlug(null)
      if (filtersRef.current && !filtersRef.current.contains(target as Node)) {
        setShowFilters(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Tab badges: one tiny request per backend status, like the totals on the finance page.
  useEffect(() => {
    let cancelled = false

    const fetchTabCounts = async () => {
      try {
        const [allRes, publishedRes, draftRes] = await Promise.all([
          blogService.getAll({ page: 1, pageSize: 1 }),
          blogService.getAll({ page: 1, pageSize: 1, status: 'published' }),
          blogService.getAll({ page: 1, pageSize: 1, status: 'draft' }),
        ])

        if (cancelled) return

        setTabCounts({
          all: allRes.data.data.total ?? 0,
          published: publishedRes.data.data.total ?? 0,
          draft: draftRes.data.data.total ?? 0,
          scheduled: 0,
        })
      } catch (error) {
        console.error('Failed to fetch article counts:', error)
      }
    }

    fetchTabCounts()

    return () => {
      cancelled = true
    }
  }, [reloadKey])

  // The table data.
  useEffect(() => {
    const status = tabStatus[activeCategory]

    if (status === null) {
      setArticles([])
      setTotalRecords(0)
      setErrorMessage(null)
      setIsLoading(false)
      return
    }

    let cancelled = false

    const loadArticles = async () => {
      setIsLoading(true)
      setErrorMessage(null)
      try {
        const res = await blogService.getAll({
          page: pageNumber,
          pageSize: PAGE_SIZE,
          status,
          category: categoryFilter || undefined,
          search: debouncedSearch || undefined,
        })
        if (cancelled) return
        setArticles(res.data.data.dataset ?? [])
        setTotalRecords(res.data.data.total ?? 0)
      } catch (error) {
        if (cancelled) return
        const message = getErrorMessage(error, 'Unable to load articles')
        console.error('Failed to fetch articles:', error)
        setArticles([])
        setTotalRecords(0)
        setErrorMessage(message)
        // call handleError(message) here
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadArticles()

    return () => {
      cancelled = true
    }
  }, [activeCategory, pageNumber, categoryFilter, debouncedSearch, reloadKey])

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalRecords / PAGE_SIZE))
  }, [totalRecords])

  const paginationItems = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)
    if (pageNumber <= 3)
      return [1, 2, 3, 'ellipsis-start', totalPages - 2, totalPages - 1, totalPages]
    if (pageNumber >= totalPages - 2) {
      return [1, 2, 3, 'ellipsis-end', totalPages - 2, totalPages - 1, totalPages]
    }
    return [
      1,
      pageNumber - 1,
      pageNumber,
      pageNumber + 1,
      'ellipsis-mid',
      totalPages - 1,
      totalPages,
    ]
  }, [pageNumber, totalPages])

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages)
    }
  }, [pageNumber, totalPages])

  const handleExport = () => {
    const table = tableSectionRef.current?.querySelector('table')
    const headerCells = Array.from(table?.tHead?.rows[0]?.cells ?? [])
    const includedColumnIndexes = headerCells
      .map((cell, index) => ({ index, label: cell.textContent?.trim() ?? '' }))
      .filter(({ label }) => !/^actions?$/i.test(label))

    if (!table || !includedColumnIndexes.length) return

    const normalizeCellText = (cell?: HTMLTableCellElement) =>
      cell?.textContent?.replace(/\s+/g, ' ').trim() ?? ''

    const rows = Array.from(table.tBodies[0]?.rows ?? [])
      .filter((row) => !row.querySelector('.blog-list__empty'))
      .map((row) => includedColumnIndexes.map(({ index }) => normalizeCellText(row.cells[index])))
      .filter((row) => row.some(Boolean))

    if (!rows.length) return

    const headers = includedColumnIndexes.map(({ label }) => label)
    const csv = Papa.unparse([headers, ...rows])
    const blob = new Blob([`${csv}`], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${exportFileName}-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  /**
   * Moves an article to the trash (recoverable on the backend, not a hard
   * delete), then refreshes the list and the tab counts.
   *
   * @param article The row being trashed; its `version` is required by the API
   */
  const handleTrash = async (article: BlogListItem) => {
    setOpenMenuSlug(null)
    if (!window.confirm(`Move "${article.title}" to trash?`)) return

    try {
      await blogService.trash(article.slug, article.version)
      // call handleSuccess('Article moved to trash') here
      setReloadKey((current) => current + 1)
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status
      if (status === 409) {
        // call handleError('This article was changed by someone else. The list has been refreshed.') here
        setReloadKey((current) => current + 1)
        return
      }
      // call handleError(getErrorMessage(error, 'Unable to move the article to trash')) here
    }
  }

  const hasActiveFilters = Boolean(categoryFilter)

  /** Message shown when the table has no rows. */
  const emptyMessage =
    activeCategory === 'scheduled'
      ? 'Scheduled publishing is not available yet.'
      : debouncedSearch || hasActiveFilters
        ? 'No articles match your search or filters.'
        : 'No articles yet. Click "New post" to write one.'

  return (
    <div className='bx-finance mx-3 mx-lg-5'>
      <div className='blog-list__header pt-4 mx-3 mx-lg-0'>
        <div>
          <h2 className='bx-finance__title mb-1'>Blog management</h2>
          <p className='blog-list__subtitle mb-0'>
            Create, edit and publish the articles shown on the TradeGrid Insights page.
          </p>
        </div>
        <button type='button' className='blog-list__new-btn' onClick={() => navigate('/blog/new')}>
          + New post
        </button>
      </div>

      <div className='bx-finance__tab-shell mt-3'>
        <div className='bx-finance__primary-tabs'>
          <div className='bx-finance__primary-tabs-inner'>
            {categoryTabs.map((tab) => (
              <button
                key={tab.key}
                type='button'
                className={`bx-finance__primary-tab ${
                  activeCategory === tab.key ? 'bx-finance__primary-tab--active' : ''
                }`}
                onClick={() => setActiveCategory(tab.key)}
              >
                {tab.label}
                <span className='blog-list__tab-count'>{tabCounts[tab.key]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className='bx-finance__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-10 col-xxl-10'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                className='bx-search-input'
                placeholder='Search by title, author or blog...'
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPageNumber(1)
                }}
              />
            </div>
          </div>

          <div className='col-6 col-md-1 position-relative' ref={filtersRef}>
            <button
              type='button'
              className='bx-action-btn w-100'
              onClick={() => setShowFilters((current) => !current)}
            >
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters ? (
              <div className='bx-finance-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Records</h5>
                  <button
                    type='button'
                    className='bx-filters-close'
                    onClick={() => setShowFilters(false)}
                  >
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Category</label>
                    <select
                      className='form-control'
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value)
                        setPageNumber(1)
                      }}
                    >
                      <option value=''>All Categories</option>
                      {CATEGORY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button
                    type='button'
                    className='btn btn-secondary'
                    onClick={() => {
                      setCategoryFilter('')
                      setShowFilters(false)
                      setPageNumber(1)
                    }}
                  >
                    Clear Filters
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => setShowFilters(false)}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className='col-6 col-md-1'>
            <button type='button' className='bx-action-btn w-100' onClick={handleExport}>
              <img src={exportIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Export</span>
            </button>
          </div>
        </div>

        <div className='bx-finance-table-section' ref={tableSectionRef}>
          <div className='blog-list__table-wrap'>
            <table className='blog-list__table'>
              <thead>
                <tr>
                  <th className='blog-list__th blog-list__th--article'>Article</th>
                  <th className='blog-list__th'>Category</th>
                  <th className='blog-list__th'>Status</th>
                  <th className='blog-list__th'>Last updated</th>
                  <th className='blog-list__th'>Author</th>
                  <th className='blog-list__th blog-list__th--actions'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className='blog-list__empty' aria-live='polite'>
                      Loading articles...
                    </td>
                  </tr>
                ) : errorMessage ? (
                  <tr>
                    <td colSpan={6} className='blog-list__empty blog-list__empty--error'>
                      {errorMessage}{' '}
                      <button
                        type='button'
                        className='blog-list__retry'
                        onClick={() => setReloadKey((current) => current + 1)}
                      >
                        Try again
                      </button>
                    </td>
                  </tr>
                ) : articles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='blog-list__empty'>
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  articles.map((article) => {
                    const isPublished = article.publishedStatus === 'published'
                    return (
                      <tr key={article.id} className='blog-list__row'>
                        <td className='blog-list__cell blog-list__cell--article'>
                          <div className='blog-list__article'>
                            {article.coverImage?.url ? (
                              <img
                                src={article.coverImage.url}
                                alt=''
                                className='blog-list__thumb'
                                loading='lazy'
                              />
                            ) : (
                              <span
                                className='blog-list__thumb blog-list__thumb--empty'
                                aria-hidden
                              />
                            )}
                            <span className='blog-list__title' title={article.title}>
                              {article.title}
                            </span>
                          </div>
                        </td>
                        <td className='blog-list__cell'>
                          {article.category ? getCategoryLabel(article.category) : '—'}
                        </td>
                        <td className='blog-list__cell'>
                          <span
                            className={`blog-list__status ${
                              isPublished
                                ? 'blog-list__status--published'
                                : 'blog-list__status--draft'
                            }`}
                          >
                            {isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className='blog-list__cell'>{formatDate(article.updatedAt)}</td>
                        <td className='blog-list__cell'>
                          <span className='blog-list__author'>
                            <span className='blog-list__avatar' aria-hidden>
                              {getInitials(article.author ?? '')}
                            </span>
                            <span className='blog-list__author-name'>{article.author}</span>
                          </span>
                        </td>
                        <td className='blog-list__cell blog-list__cell--actions'>
                          <button
                            type='button'
                            className='blog-list__edit-btn'
                            onClick={() => navigate(`/blog-editor/edit/${article.slug}`)}
                          >
                            Edit
                          </button>
                          <div className='blog-list__menu'>
                            <button
                              type='button'
                              className='blog-list__menu-btn'
                              aria-label={`More actions for ${article.title}`}
                              aria-haspopup='menu'
                              aria-expanded={openMenuSlug === article.slug}
                              onClick={() =>
                                setOpenMenuSlug((current) =>
                                  current === article.slug ? null : article.slug,
                                )
                              }
                            >
                              ···
                            </button>
                            {openMenuSlug === article.slug ? (
                              <div className='blog-list__menu-list' role='menu'>
                                <button
                                  type='button'
                                  role='menuitem'
                                  className='blog-list__menu-item blog-list__menu-item--danger'
                                  onClick={() => handleTrash(article)}
                                >
                                  Move to trash
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='bx-pagination mb-5'>
          <button
            type='button'
            className='bx-pagination-btn'
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
          >
            <span className='bx-pagination-icon-wrap'>
              <img src={arrowLeft} alt='' className='bx-pagination-icon bx-pagination-icon--left' />
            </span>
            <span className='bx-pagination-text'>Previous</span>
          </button>

          <div className='bx-pagination-numbers d-none d-md-flex'>
            {paginationItems.map((item, index) =>
              typeof item === 'number' ? (
                <button
                  key={item}
                  type='button'
                  className={`bx-page-number ${pageNumber === item ? 'bx-page-number--active' : ''}`}
                  onClick={() => setPageNumber(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={`${item}-${index}`} className='bx-page-ellipsis'>
                  ...
                </span>
              ),
            )}
          </div>

          <button
            type='button'
            className='bx-pagination-btn'
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber((current) => Math.min(totalPages, current + 1))}
          >
            <span className='bx-pagination-text'>Next</span>
            <span className='bx-pagination-icon-wrap'>
              <img src={arrowRight} alt='' className='bx-pagination-icon' />
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default BlogDetails
